import { MutableRefObject, forwardRef, useEffect, useMemo, useRef } from 'react';
import { Row, TableToggleAllRowsSelectedProps, useRowSelect, useSortBy, useTable } from 'react-table';
import styles from './Table.module.scss';
import { useLazyGetShipInfoQuery } from 'app/api/ships';
import { useAppDispatch } from 'app/store/store';
import { ReactComponent as SortIconUp } from 'app/assets/icons/icon-chevron-fill-up.svg';
import { ReactComponent as SortIconDown } from 'app/assets/icons/icon-chevron-fill-down.svg';
import { SortingInterface } from 'app/pages/VesselsPage/VesselsPage';
import { setSelectedGroup, setSelectedMmsi, setSelectedNtfRules, setSelectedRow } from 'app/utils/tableSlice';

import { PortsSortingInterface } from 'app/pages/PortsPage/PortsPage';

import { FleetInfo, NotificationRule, OwnedTargetInfo, PortCatalogData, GroupInfo } from 'ais-facade-api/api';
import { useTranslation } from 'react-i18next';
import { PortArrivalsSortingInterface } from 'app/pages/PortDetails/PortArrivals';
import { TargetCatalogSortingInterface } from '../VesselsCatalogModal/VesselsCatalogModal';
import { setSelectedTarget } from 'app/utils/selectedTargetsSlice';
import { useLazyGetPortQuery } from 'app/api/marine-infrastructure';
import clsx from 'clsx';

interface Props {
  columns: any[];
  data: any[];
  withCheckbox?: boolean;
  withFooter?: boolean;
  withSelect?: boolean;
  manualSort?: boolean;
  manualPagination?: boolean;
  onClick?: (props?: any) => void;
  sorting?: SortingInterface | PortsSortingInterface | PortArrivalsSortingInterface | TargetCatalogSortingInterface;
  setSorting?: any;
  withScroll?: boolean;
  total?: number;
}

const IndeterminateCheckbox = forwardRef(
  ({ indeterminate, className, ...rest }: Partial<TableToggleAllRowsSelectedProps>, ref) => {
    const defaultRef = useRef<HTMLInputElement | null>(null);
    const resolvedRef = (ref as MutableRefObject<HTMLInputElement | null>) || defaultRef;

    useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate as boolean;
      }
    }, [resolvedRef, indeterminate]);

    return <input className={className} type="checkbox" ref={resolvedRef} {...rest} />;
  },
);

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

export const Table = ({
  columns,
  data,
  sorting,
  withSelect,
  withCheckbox,
  setSorting,
  manualSort = false,
  manualPagination = false,
  withFooter = false,
  onClick,
}: Props) => {
  const { t } = useTranslation();
  const columnData = useMemo(() => columns, [columns]);
  const rowData = useMemo(() => data, [data]);
  const dispatch = useAppDispatch();
  const [getVesselInfo] = useLazyGetShipInfoQuery();
  const [getPortInfo] = useLazyGetPortQuery();
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns: columnData,
      data: rowData,
      manualPagination,
    },
    useSortBy,

    useRowSelect,
    ({ visibleColumns }) => {
      if (withCheckbox) {
        visibleColumns.push((cols) => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }: any) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} title={t('SelectAllVessels')} />
            ),
            Cell: ({ row }: { row: Row<FleetInfo> }) => (
              <IndeterminateCheckbox
                className={styles.input_cell}
                {...row.getToggleRowSelectedProps()}
                title={t('SelectVessel')}
              />
            ),
          },
          ...cols,
        ]);
      }
    },
  );

  const handleCheckboxSet = (row: Row, event: React.MouseEvent<HTMLTableRowElement>) => {
    const isAnchorElement = event.target instanceof HTMLAnchorElement;
    const isSvgElement = event.target instanceof SVGElement;
    const isButtonElement = event.target instanceof HTMLButtonElement;
    if (isAnchorElement || isSvgElement || isButtonElement) return;
    row.toggleRowSelected();
  };

  const handleSelectRow = (row: Row) => {
    toggleAllRowsSelected(false);
    dispatch(setSelectedRow(null));

    if (!row.isSelected) {
      row.toggleRowSelected();
      dispatch(setSelectedRow(row.original));
    }
  };

  const handleRowClick = (row: Row<OwnedTargetInfo & PortCatalogData>) => {
    if (row.original.mmsi)
      getVesselInfo(row.original.mmsi).then((res) => {
        const target = res.data?.result;
        if (target?.registryData?.specialType && target?.registryData?.specialType === 'ATON') {
          dispatch(setSelectedTarget({ target: { object: target }, type: 'aton' }));
        } else {
          dispatch(setSelectedTarget({ target: { object: target }, type: 'vessel' }));
        }
      });
    if (row.original.key) {
      getPortInfo(row.original.key).then((res) => {
        const target = res.data?.port;
        dispatch(setSelectedTarget({ target: { object: target }, type: 'port' }));
      });
    }
    if (onClick) {
      onClick(row.original);
    }
  };

  const onSortChange = (prev: SortingInterface | PortsSortingInterface, sortBy: string) => {
    let nextDirection: 'ASC' | 'DESC' | 'default' = 'default';

    if (prev.direction === 'DESC') nextDirection = 'ASC';
    else if (prev.direction === 'ASC') nextDirection = 'default';
    else if (prev.direction === 'default') nextDirection = 'DESC';

    return {
      direction: nextDirection,
      by: sortBy,
    };
  };
  useEffect(() => {
    const mmsiArray: number[] = [];
    const groupArray: GroupInfo[] = [];
    const ntfRulesArray: string[] = [];

    const fromOriginal = selectedFlatRows.map((item) => item.original);
    fromOriginal.map((fr: FleetInfo | NotificationRule) => {
      if ('mmsi' in fr && typeof fr.mmsi === 'number') {
        mmsiArray.push(fr.mmsi);
      }
      if ('groups' in fr && fr.groups && typeof fr.groups[0]?.key === 'string') {
        groupArray.push(fr.groups[0]);
      }
      if ('eventType' in fr && typeof fr.key === 'string') {
        ntfRulesArray.push(fr.key);
      }
    });
    dispatch(setSelectedMmsi(mmsiArray));
    dispatch(setSelectedGroup(groupArray));
    dispatch(setSelectedNtfRules(ntfRulesArray));
  }, [selectedFlatRows]);

  return (
    <div className={styles.table_wrapper}>
      <table {...getTableProps()} className={styles.table}>
        <thead className={styles.table_head}>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index} className={styles.table_row}>
              {headerGroup.headers.map((column, index) => {
                const isSorted = 'sortBy' in column && column['sortBy'] === sorting?.by;
                const headerProps = !manualSort ? column.getHeaderProps(column.getSortByToggleProps()) : column.getHeaderProps();

                const onClickFunc = !manualSort
                  ? () => {
                      column?.toggleSortBy && column?.toggleSortBy();
                    }
                  : () => {
                      if (manualSort && sorting && setSorting && 'sortBy' in column) {
                        setSorting((prev: SortingInterface | PortsSortingInterface) =>
                          onSortChange(prev, column['sortBy'] as string),
                        );
                      }
                    };

                const isCheckboxHeading = column?.id === 'selection';

                return (
                  <th
                    {...headerProps}
                    key={index}
                    data-checkbox={withCheckbox}
                    className={styles.table_heading}
                    onClick={!isCheckboxHeading ? onClickFunc : undefined}
                  >
                    <div className={clsx(styles.heading_cell, { [styles.sortable]: column.canSort })}>
                      {typeof column.render('Header') === 'string' && column.Header
                        ? t(column.Header.toString())
                        : column.render('Header')}
                      {manualSort
                        ? column.canSort &&
                          'sortBy' in column && (
                            <span className={styles.sort}>
                              {(!isSorted || sorting?.direction === 'default') && (
                                <>
                                  <SortIconUp />
                                  <SortIconDown />
                                </>
                              )}

                              {isSorted && sorting?.direction === 'ASC' && <SortIconUp />}
                              {isSorted && sorting?.direction === 'DESC' && <SortIconDown />}
                            </span>
                          )
                        : column.canSort && (
                            <>
                              {column.isSorted ? (
                                column.isSortedDesc ? (
                                  <span className={styles.sort}>
                                    <SortIconUp />
                                  </span>
                                ) : (
                                  <span className={styles.sort}>
                                    <SortIconDown />
                                  </span>
                                )
                              ) : (
                                <span className={styles.sort}>
                                  <SortIconUp />
                                  <SortIconDown />
                                </span>
                              )}
                            </>
                          )}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()} className={styles.table_body}>
          {rows.map((row, i) => {
            prepareRow(row);
            return (
              <tr
                {...row.getRowProps()}
                key={i}
                className={styles.table_row}
                data-selected={row.isSelected}
                data-checkbox={withCheckbox}
                onClick={
                  withCheckbox
                    ? (event) => handleCheckboxSet(row, event)
                    : withSelect
                      ? () => handleSelectRow(row)
                      : onClick
                        ? () => handleRowClick(row)
                        : undefined
                }
              >
                {row.cells.map((cell, i) => {
                  return (
                    <td
                      {...cell.getCellProps({
                        style: {
                          minWidth: cell.column.minWidth === 0 ? 'fit-content' : cell.column.minWidth,
                          width: cell.column.width === 150 ? 'fit-content' : cell.column.width,
                          maxWidth: cell.column.totalWidth === 150 ? '400px' : cell.column.maxWidth,
                        },
                      })}
                      key={i}
                      className={styles.table_cell}
                    >
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        {withFooter && (
          <tfoot className={styles.table_footer}>
            {footerGroups.map((group, i) => (
              <tr {...group.getFooterGroupProps()} key={i} className={styles.table_row}>
                {group.headers.map((column, j) => (
                  <td {...column.getFooterProps()} key={`${i}-${j}`} className={styles.table_cell}>
                    {column.render('Footer')}
                  </td>
                ))}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  );
};
